import { useEffect, useMemo, useState } from "react";
import {
  App as AntApp, Alert, Button, Form, Input, Modal, Typography,
} from "antd";
import {
  BookOutlined,
  CodeOutlined,
  CopyOutlined,
  DeleteOutlined,
  ExportOutlined,
  KeyOutlined,
  PlusOutlined,
  RightOutlined,
} from "@ant-design/icons";

import { api, ApiError } from "../api";
import { ScraperDot } from "../components/ScraperTag";
import type { ApiKey, CreatedApiKey, ScraperSchema } from "../types";

type Language = "curl" | "python" | "typescript";

export function ApiPage() {
  const { message, modal } = AntApp.useApp();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [scrapers, setScrapers] = useState<ScraperSchema[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newlyCreated, setNewlyCreated] = useState<CreatedApiKey | null>(null);
  const [language, setLanguage] = useState<Language>("curl");
  const [form] = Form.useForm<{ label: string }>();

  async function refresh() {
    const [k, s] = await Promise.all([api.listApiKeys(), api.scrapers()]);
    setKeys(k.keys);
    setScrapers(s.scrapers);
    setLoading(false);
  }

  useEffect(() => {
    refresh().catch((err) => {
      setLoading(false);
      message.error(err instanceof ApiError ? err.message : "Load failed");
    });
  }, [message]);

  async function handleCreate() {
    const values = await form.validateFields();
    try {
      const created = await api.createApiKey(values.label);
      setNewlyCreated(created);
      setCreateOpen(false);
      form.resetFields();
      await refresh();
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "Create failed");
    }
  }

  function handleRevoke(id: string) {
    modal.confirm({
      title: "Revoke API key?",
      content: "Clients using it will be rejected immediately.",
      okText: "Revoke",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await api.revokeApiKey(id);
          message.success("Key revoked");
          await refresh();
        } catch {
          message.error("Revoke failed");
        }
      },
    });
  }

  const activeCount = useMemo(() => keys.filter((k) => !k.revoked_at).length, [keys]);
  const firstScraperName = scrapers[0]?.name ?? "mobilesentrix";
  const codeSamples = useMemo(
    () => ({
      curl: curlExample(firstScraperName),
      python: pythonExample(firstScraperName),
      typescript: tsExample(firstScraperName),
    }),
    [firstScraperName],
  );

  return (
    <>
      <div className="crl-page-top">
        <div className="crl-breadcrumb">
          <span>Workspace</span>
          <RightOutlined className="crl-chev" style={{ fontSize: 10 }} />
          <span className="crl-cur">API & Keys</span>
        </div>
        <div className="crl-page-top-spacer" />
        <Button size="small" type="text" icon={<BookOutlined />}>
          Docs
        </Button>
        <Button
          size="small"
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setCreateOpen(true)}
        >
          Generate key
        </Button>
      </div>

      <div className="crl-page-header">
        <h1 className="crl-page-title">
          <span className="crl-title-icon">
            <KeyOutlined style={{ fontSize: 15 }} />
          </span>
          API & Keys
        </h1>
        <div className="crl-page-sub">
          Authenticate requests with a bearer token. Each workspace has independent keys.
        </div>
      </div>

      <div className="crl-api-layout">
        {newlyCreated && (
          <Alert
            type="success"
            showIcon
            closable
            onClose={() => setNewlyCreated(null)}
            style={{ marginBottom: 14, borderRadius: 8 }}
            message="New key created — copy it now, it won't be shown again"
            description={
              <Typography.Text
                code
                copyable={{ text: newlyCreated.key, tooltips: ["Copy", "Copied!"] }}
              >
                {newlyCreated.key}
              </Typography.Text>
            }
          />
        )}

        <div className="crl-section-head">
          <h3>API keys</h3>
          <span className="crl-meta">
            {keys.length} key{keys.length === 1 ? "" : "s"} · {activeCount} active
          </span>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "var(--text-tertiary)" }}>
            Loading…
          </div>
        ) : keys.length === 0 ? (
          <div
            style={{
              padding: 28,
              textAlign: "center",
              border: "1px dashed var(--border)",
              borderRadius: 8,
              color: "var(--text-tertiary)",
              fontSize: 13,
            }}
          >
            No API keys yet. Click <b style={{ color: "var(--text-secondary)" }}>Generate key</b> to
            create one.
          </div>
        ) : (
          keys.map((k) => (
            <div
              key={k.id}
              className="crl-key-card"
              style={{ opacity: k.revoked_at ? 0.65 : 1 }}
            >
              <div
                className="crl-icon-badge"
                style={
                  k.revoked_at
                    ? { background: "var(--accent-red-bg)", color: "var(--accent-red)" }
                    : undefined
                }
              >
                <KeyOutlined />
              </div>
              <div>
                <div className="crl-name">{k.label}</div>
                <div className="crl-sub">{k.key_prefix}… · created {formatDate(k.created_at)}</div>
              </div>
              <div className="crl-spacer" />
              <div className="crl-used">
                {k.last_used_at ? `Used ${timeAgo(k.last_used_at)}` : "Never used"}
              </div>
              <span className={`crl-pill ${k.revoked_at ? "revoked" : "active-status"}`}>
                <span className="crl-pdot" />
                {k.revoked_at ? "Revoked" : "Active"}
              </span>
              {!k.revoked_at && (
                <Button
                  size="small"
                  danger
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => handleRevoke(k.id)}
                >
                  Revoke
                </Button>
              )}
            </div>
          ))
        )}

        <div className="crl-section-head">
          <h3>Quickstart</h3>
          <div style={{ display: "flex", gap: 4 }}>
            <LanguageChip value="curl" active={language} onClick={setLanguage}>
              curl
            </LanguageChip>
            <LanguageChip value="python" active={language} onClick={setLanguage}>
              Python
            </LanguageChip>
            <LanguageChip value="typescript" active={language} onClick={setLanguage}>
              TypeScript
            </LanguageChip>
          </div>
        </div>

        <CodeBlock
          lang={language === "curl" ? "POST · start a scrape" : language === "python" ? "Python · poll to completion" : "TypeScript · poll to completion"}
          code={codeSamples[language]}
        />

        {language === "curl" && (
          <CodeBlock lang="GET · poll job" code={pollExample()} />
        )}

        {scrapers.length > 0 && (
          <>
            <div className="crl-section-head">
              <h3>Available scrapers</h3>
            </div>
            <div className="crl-scrapers-catalog">
              {scrapers.map((s) => (
                <div key={s.name} className="crl-scraper-tile">
                  <div className="crl-top">
                    <ScraperDot name={s.name} />
                    <span className="crl-nm">{s.label}</span>
                    <ExportOutlined style={{ color: "var(--text-tertiary)", fontSize: 11 }} />
                  </div>
                  <div className="crl-ds">{s.description}</div>
                  <div className="crl-fd">
                    {s.fields.map((f) => f.key).join(" · ")}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        <div style={{ height: 40 }} />
      </div>

      <Modal
        open={createOpen}
        title="Generate API key"
        onOk={handleCreate}
        onCancel={() => {
          setCreateOpen(false);
          form.resetFields();
        }}
        okText="Generate"
      >
        <Form form={form} layout="vertical" initialValues={{ label: "default" }}>
          <Form.Item
            name="label"
            label="Label"
            rules={[{ required: true, message: "Label required" }]}
            extra="Human-readable tag, e.g. 'twenty-crm', 'cli-dev'"
          >
            <Input placeholder="twenty-crm-integration" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

function LanguageChip({
  value,
  active,
  onClick,
  children,
}: {
  value: Language;
  active: Language;
  onClick: (v: Language) => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className={`crl-chip${value === active ? " active" : ""}`}
      onClick={() => onClick(value)}
    >
      {children}
    </button>
  );
}

function CodeBlock({ lang, code }: { lang: string; code: string }) {
  const { message } = AntApp.useApp();
  return (
    <div className="crl-code-block">
      <div className="crl-code-head">
        <CodeOutlined style={{ color: "var(--text-tertiary)" }} />
        <span className="crl-lang">{lang}</span>
        <div className="crl-spacer" />
        <Button
          size="small"
          type="text"
          icon={<CopyOutlined />}
          onClick={() =>
            navigator.clipboard.writeText(code).then(() => message.success("Copied"))
          }
        />
      </div>
      <pre>{code}</pre>
    </div>
  );
}

function curlExample(name: string): string {
  return `# Send as Authorization: Bearer <key>
curl -X POST https://crl.vasilisa.at/scrape/${name} \\
  -H "Authorization: Bearer $CRL_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"input": {"mode": "sku", "sku": "SAM-GAL-S24"}}'`;
}

function pollExample(): string {
  return `curl https://crl.vasilisa.at/jobs/<job_id> \\
  -H "Authorization: Bearer $CRL_KEY"`;
}

function pythonExample(name: string): string {
  return `import requests, time

API = "https://crl.vasilisa.at"
KEY = "crl_YOUR_KEY"
headers = {"Authorization": f"Bearer {KEY}"}

r = requests.post(f"{API}/scrape/${name}",
                  json={"input": {"mode": "filter"}},
                  headers=headers)
job_id = r.json()["id"]

while True:
    r = requests.get(f"{API}/jobs/{job_id}", headers=headers)
    job = r.json()
    if job["status"] in ("done", "failed"):
        break
    time.sleep(2)

print(f"{len(job.get('result', []))} items")`;
}

function tsExample(name: string): string {
  return `const API = "https://crl.vasilisa.at";
const KEY = "crl_YOUR_KEY";
const h = { Authorization: \`Bearer \${KEY}\`, "Content-Type": "application/json" };

const start = await fetch(\`\${API}/scrape/${name}\`, {
  method: "POST", headers: h,
  body: JSON.stringify({ input: { mode: "filter" } }),
});
const { id } = await start.json();

while (true) {
  const r = await fetch(\`\${API}/jobs/\${id}\`, { headers: h });
  const job = await r.json();
  if (job.status === "done" || job.status === "failed") {
    console.log(job.result?.length ?? 0, "items");
    break;
  }
  await new Promise((r) => setTimeout(r, 2000));
}`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "2-digit", year: "numeric" });
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
